/**
 * Ory Kratos browser registration flow, driven from the SPA.
 *
 * Password registration does not sign the user in. A 200 with
 * continue_with show_verification_ui means the inbox code still has to match.
 * The verification session hook issues the cookie only after that match.
 */

import { HID_EMAIL_MESSAGE } from "./hidEmail";
import { LoginFlow, csrfToken, messagesOf } from "./kratosLogin";

const KRATOS =
  process.env.REACT_APP_KRATOS_PUBLIC_URL || "http://localhost:4433";

export type RegistrationFlow = LoginFlow;

export type RegistrationResult =
  | { kind: "success" }
  | { kind: "verified" }
  | { kind: "verify"; verificationFlowId?: string }
  | { kind: "alreadySignedIn" }
  | { kind: "expired" }
  | { kind: "error"; flow?: RegistrationFlow; message: string };

export class AlreadySignedIn extends Error {
  constructor() {
    super("A session already exists");
  }
}

export async function createRegistrationFlow(): Promise<RegistrationFlow> {
  const res = await fetch(`${KRATOS}/self-service/registration/browser`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) {
    const payload = (await res.json().catch(() => null)) as { error?: { id?: string } } | null;
    if (payload?.error?.id === "session_already_available") throw new AlreadySignedIn();
    throw new Error(`Could not start a registration flow (HTTP ${res.status})`);
  }
  return (await res.json()) as RegistrationFlow;
}

export async function registerWithPassword(
  flow: RegistrationFlow,
  email: string,
  password: string
): Promise<RegistrationResult> {
  const res = await fetch(flow.ui.action, {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      method: "password",
      csrf_token: csrfToken(flow),
      traits: { email },
      password,
    }),
  });

  if (res.ok) {
    const body = (await res.json().catch(() => null)) as {
      continue_with?: { action?: string; flow?: { id?: string } }[];
    } | null;
    const verify = body?.continue_with?.find((item) => item.action === "show_verification_ui");
    if (verify) return { kind: "verify", verificationFlowId: verify.flow?.id };
    return { kind: "success" };
  }

  const payload = (await res.json().catch(() => null)) as
    | (Partial<RegistrationFlow> & { error?: { id?: string; message?: string } })
    | null;

  if (payload?.error?.id === "session_already_available") return { kind: "alreadySignedIn" };
  if (res.status === 410 || payload?.error?.id === "self_service_flow_expired") {
    return { kind: "expired" };
  }
  if (!payload?.ui) {
    return {
      kind: "error",
      message: payload?.error?.message || "Registration failed. Please try again.",
    };
  }

  const updated = payload as RegistrationFlow;
  return {
    kind: "error",
    flow: updated,
    message:
      friendlyRegistrationMessage(
        messagesOf(updated, "error")[0] ||
          messagesOf(updated, "info")[0] ||
          "Registration failed. Please check your details and try again."
      ),
  };
}

function friendlyRegistrationMessage(message: string): string {
  if (/pattern|does not match/i.test(message)) return HID_EMAIL_MESSAGE;
  return message;
}

async function verificationFlow(flowId?: string): Promise<RegistrationFlow> {
  const path = flowId
    ? `/self-service/verification/flows?id=${encodeURIComponent(flowId)}`
    : "/self-service/verification/browser";
  const res = await fetch(`${KRATOS}${path}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) throw new Error("Could not start email verification.");
  return (await res.json()) as RegistrationFlow;
}

/** Submit the code Kratos emailed. A match marks the address verified (no session in v1.3.1). */
export async function submitRegistrationCode(
  flowId: string | undefined,
  code: string
): Promise<RegistrationResult> {
  const flow = await verificationFlow(flowId);
  const res = await fetch(flow.ui.action, {
    method: "POST",
    credentials: "include",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify({ method: "code", code, csrf_token: csrfToken(flow) }),
  });
  if (res.ok) return { kind: "verified" };
  const payload = (await res.json().catch(() => null)) as Partial<RegistrationFlow> | null;
  // Some Kratos responses return the completed flow with state=passed_challenge.
  if (payload && (payload as { state?: string }).state === "passed_challenge") {
    return { kind: "verified" };
  }
  if (!payload?.ui) return { kind: "error", message: "The code could not be verified. Request a new one." };
  return {
    kind: "error",
    flow: payload as RegistrationFlow,
    message: messagesOf(payload as RegistrationFlow, "error")[0] || "The code is invalid or has expired.",
  };
}
